import { resolveLabel, resolveLoc } from "context/TestDataContext";
import { calcAge, formatDateEU } from "utils/dateUtils";

// Extract row number from a cell address like "G7" → 7
const rowFromCell = (cellAddr) => parseInt(cellAddr.replace(/[A-Z]+/i, ""), 10);

// Excel template sections per language. headerRows stay visible (or get hidden
// when no test in that section is selected), testRange rows toggle individually.
const templateSectionsByLang = {
  fr: [
    { headerRows: [6], testRange: [7, 19] },            // Attention
    { headerRows: [20], testRange: [21, 36] },           // Fonctions exécutives
    { headerRows: [37], testRange: [38, 41] },           // Orientation
    { headerRows: [42], testRange: [43, 68] },           // Mémoire verbale
    { headerRows: [69], testRange: [70, 76] },           // Mémoire visuelle
    { headerRows: [77], testRange: [78, 93] },           // Traitement visuo-spatial et praxies constructives
    { headerRows: [94], testRange: [95, 100] },          // Praxies gestuelles
    { headerRows: [101], testRange: [102, 113] },        // Langage oral et écrit
    { headerRows: [114], testRange: [115, 115] },        // Calcul
    { headerRows: [116], testRange: [117, 119] },        // Performances intellectuelles générales
    { headerRows: [120], testRange: [121, 128] },        // Questionnaires
  ],
  de: [
    { headerRows: [6], testRange: [7, 17] },             // Aufmerksamkeitsleistungen
    { headerRows: [18], testRange: [19, 33] },           // Exekutivfunktionen
    { headerRows: [34], testRange: [35, 38] },           // Orientierungsleistungen
    { headerRows: [39], testRange: [40, 66] },           // Verbales Gedächtnis (incl. WMS-IV LG rows 59-66)
    { headerRows: [67], testRange: [68, 74] },           // Visuelles Gedächtnis
    { headerRows: [75], testRange: [76, 89] },           // Visuell-räumliche und visuokonstruktive Leistungen
    { headerRows: [90], testRange: [91, 97] },           // Praxien
    { headerRows: [98], testRange: [99, 111] },          // Sprachleistungen
    { headerRows: [112], testRange: [113, 114] },        // Rechenleistungen
    { headerRows: [115], testRange: [116, 118] },        // Allgemeine kognitive Leistungsfähigkeit
    { headerRows: [119], testRange: [120, 129] },        // Fragebogen (incl. NOSGER 127, CDR 128, BDQ 129)
  ],
};

// FR Excel row → DE Excel row. Used to translate legacy single-language
// resultLoc strings produced by scoring functions (calcCERAD, calcWAISCode,
// etc.) into the DE template's row layout. Per-test bilingual resultLoc
// overrides defined in defaultTests take precedence — only plain strings hit
// this map.
const frToDeRow = {
  // Attention section (FR rows 7-19)
  7: 7, 8: 8, 9: 9, 10: 10, 11: 11, 12: 13, 13: 12,
  14: 14, 15: 15, 16: 16, 17: null, 18: 17, 19: null,
  // Fonctions exécutives (FR rows 21-36)
  21: 19, 22: null, 23: 20, 24: 21, 25: 22, 26: 23,
  27: 24, 28: 25, 29: 26, 30: 27, 31: 28, 32: 29, 33: 30, 34: 31, 35: 32, 36: 33,
  // Orientation (FR rows 38-41)
  38: 35, 39: 36, 40: 37, 41: 38,
  // Mémoire verbale (FR rows 43-68)
  43: 40, 44: null,
  45: 41, 46: 42, 47: 43,
  48: 44, 49: 45, 50: 46, 51: 47, 52: 48,
  53: 49, 54: 50, 55: 51, 56: 52, 57: 53,
  58: 54, 59: 55, 60: 56, 61: 57, 62: 58,
  63: null, 64: null, 65: null, 66: null, 67: 62, 68: 63,
  // Mémoire visuelle (FR rows 70-76)
  70: 65, 71: 66, 72: 67, 73: 69, 74: 70, 75: null, 76: 71,
  // Traitement visuo-spatial (FR rows 78-93)
  78: null, 79: null, 80: null,
  81: 73, 82: 74, 83: 75, 84: 76, 85: 80,
  86: 81, 87: 83, 88: null, 89: null, 90: null, 91: null, 92: null, 93: null,
  // Praxies (FR rows 95-100) — Macoir 2025 rows (98-100) have no DE counterpart
  95: 89, 96: 90, 97: 91, 98: null, 99: null, 100: null,
  // Langage (FR rows 102-113)
  102: 96, 103: 97, 104: 98, 105: 99, 106: 100, 107: 101,
  108: 102, 109: null, 110: null, 111: null, 112: null, 113: 107,
  // Calcul (FR row 115)
  115: 110,
  // Cognitive global (FR rows 117-118)
  117: 113, 118: 114,
  // Questionnaires (FR rows 121-128)
  121: 118, 122: 119, 123: null, 124: null,
  125: 121, 126: null, 127: 122, 128: 125,
};

const translateRow = (cellStr, lang) => {
  if (lang !== "de") return cellStr;
  const m = cellStr.match(/^([A-Z]+)(\d+)$/);
  if (!m) return cellStr;
  const col = m[1];
  const row = parseInt(m[2], 10);
  const mapped = frToDeRow[row];
  if (mapped == null) return null; // no DE counterpart
  return `${col}${mapped}`;
};

// Look up the location field that may be a per-language object or a legacy
// plain cell-address string. Plain strings are treated as FR rows and
// translated to DE via frToDeRow when needed.
const pickLoc = (field, lang) => {
  if (!field) return undefined;
  if (typeof field === "string") return translateRow(field, lang);
  return field[lang] ?? null;
};

// Pair the col letter of `srcLoc` (FR-flavored evaluation cell) with the row
// of `targetLoc` (already per-language) so the eval mark lands on the right row.
const composeEvalLoc = (srcLoc, targetLoc) => {
  if (!srcLoc || !targetLoc) return null;
  const col = srcLoc.match(/^[A-Z]+/)?.[0];
  const row = targetLoc.match(/\d+$/)?.[0];
  if (!col || !row) return null;
  return `${col}${row}`;
};

// Translate a FR-flavored "I44:J44" merge span (same row convention as
// evaluation.loc) onto the resolved per-language evalLoc's row.
const composeMergeRange = (mergeRangeRaw, evalLoc) => {
  if (!mergeRangeRaw || !evalLoc) return null;
  const [startRaw, endRaw] = mergeRangeRaw.split(":");
  const colStart = startRaw?.match(/^[A-Z]+/)?.[0];
  const colEnd = endRaw?.match(/^[A-Z]+/)?.[0];
  const row = evalLoc.match(/\d+$/)?.[0];
  if (!colStart || !colEnd || !row) return null;
  return `${colStart}${row}:${colEnd}${row}`;
};

export const buildCellEdits = (patientInfo, testData, profileTests, lang = "fr") => {
  const cellEdits = {};
  const hiddenRows = [];
  const mergeRanges = [];
  const profileTestSet = profileTests ? new Set(profileTests) : null;
  const templateSections = templateSectionsByLang[lang] || templateSectionsByLang.fr;

  // Patient info → row 2 (column letters are identical between FR and DE templates)
  if (patientInfo) {
    if (patientInfo.nom) cellEdits["A2"] = patientInfo.nom;
    if (patientInfo.prenom) cellEdits["C2"] = patientInfo.prenom;
    if (patientInfo.dateNaissance) {
      const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);
      const ageStr = age !== null ? ` (${age})` : "";
      cellEdits["D2"] = formatDateEU(patientInfo.dateNaissance) + ageStr;
    }
    if (patientInfo.dateExamen) {
      let examDate = formatDateEU(patientInfo.dateExamen);
      if (patientInfo.dateExamenComparatif1)
        examDate += `\n(${formatDateEU(patientInfo.dateExamenComparatif1)})`;
      if (patientInfo.dateExamenComparatif2)
        examDate += `\n(${formatDateEU(patientInfo.dateExamenComparatif2)})`;
      cellEdits["E2"] = examDate;
    }
    if (patientInfo.lateralite) cellEdits["G2"] = patientInfo.lateralite;
    if (lang === "de") {
      if (patientInfo.anneesEtudes) cellEdits["H2"] = patientInfo.anneesEtudes;
    } else {
      if (patientInfo.niveau) cellEdits["H2"] = patientInfo.niveau;
    }
    if (patientInfo.langueMaternelle)
      cellEdits["N2"] = patientInfo.langueMaternelle;
  }

  // Build set of profile test rows (rows to keep visible)
  const keepRows = new Set();
  const skippedNoMapping = [];
  for (const [testName, entry] of Object.entries(testData)) {
    const inProfile = !profileTestSet || profileTestSet.has(testName);
    if (!inProfile) continue;

    if (entry.results) {
      for (const result of entry.results) {
        const loc = pickLoc(result.resultLoc, lang);
        if (loc) keepRows.add(rowFromCell(loc));
      }
      continue;
    }

    const loc = pickLoc(entry.resultLoc, lang);
    if (loc) {
      keepRows.add(rowFromCell(loc));
    } else if (entry.resultLoc && pickLoc(entry.resultLoc, lang === "fr" ? "de" : "fr")) {
      // Test exists for the other language but not this one — record so we can warn.
      skippedNoMapping.push(testName);
    }
  }

  // Excludes `exceptCol` so the caller can write the eval mark afterward as a
  // fresh insertion — when the mark targets a merged cell (e.g. I:J), an
  // earlier "" write to the slave cell would otherwise overwrite the master.
  const clearEvalRow = (rowNum, exceptCol) => {
    for (const col of ["I", "J", "K", "L", "M"]) {
      if (col === exceptCol) continue;
      cellEdits[`${col}${rowNum}`] = "";
    }
  };

  const evalCol = (locStr) => locStr?.match(/^[A-Z]+/)?.[0];

  // Write cell values for profile tests
  for (const [testName, entry] of Object.entries(testData)) {
    const inProfile = !profileTestSet || profileTestSet.has(testName);
    if (!inProfile) continue;

    if (entry.results) {
      for (const result of entry.results) {
        const resultLoc = pickLoc(result.resultLoc, lang);
        if (!resultLoc) continue;
        const evalLocRaw = result.evaluation?.loc;
        const evalLoc = evalLocRaw ? composeEvalLoc(evalLocRaw, resultLoc) : null;
        clearEvalRow(rowFromCell(resultLoc), evalCol(evalLoc));
        // Write eval mark FIRST so resultText wins when both target the same merged cell
        if (result.evaluation && evalLoc) {
          cellEdits[evalLoc] = result.evaluation.mark;
          const mergeRange = composeMergeRange(result.evaluation.mergeRange, evalLoc);
          if (mergeRange) mergeRanges.push(mergeRange);
        }
        if (result.resultText) {
          cellEdits[resultLoc] = result.resultText;
        }
      }
      continue;
    }

    const entryLoc = pickLoc(entry.resultLoc, lang);
    if (!entryLoc) continue;

    const evalLocRaw = entry.evaluation?.loc;
    const evalLoc = evalLocRaw ? composeEvalLoc(evalLocRaw, entryLoc) : null;
    clearEvalRow(rowFromCell(entryLoc), evalCol(evalLoc));

    if (entry.evaluation && evalLoc) {
      cellEdits[evalLoc] = entry.evaluation.mark;
      const mergeRange = composeMergeRange(entry.evaluation.mergeRange, evalLoc);
      if (mergeRange) mergeRanges.push(mergeRange);
    }

    if (entry.resultText) {
      let cellValue = entry.resultText;
      const significatifToggles = (entry.params || []).filter(
        (p) => p.type === "significatifToggle" && p.value
      );
      if (significatifToggles.length) {
        const translateSig = (v) => {
          if (lang !== "de") return v;
          if (v === "significative") return "signifikant";
          if (v === "pas significative") return "nicht signifikant";
          return v;
        };
        const toggleLines = significatifToggles
          .map((p) => `${resolveLabel(p.label, lang)} ${translateSig(p.value)}`)
          .join("\n");
        cellValue = `${cellValue}\n${toggleLines}`;
      }
      if (entry.comment) {
        const noteHeader = lang === "de" ? "Klinische Notiz" : "Note clinique";
        const commentSuffix = `\n\n${noteHeader}:\n` + entry.comment;
        const commentRun = {
          text: commentSuffix,
          font: { italic: true, color: { argb: "FF000000" } },
        };
        if (cellValue && typeof cellValue === "object" && Array.isArray(cellValue.richText)) {
          cellValue = { richText: [...cellValue.richText, commentRun] };
        } else {
          cellValue = {
            richText: [
              { text: String(cellValue), font: { color: { argb: "FF000000" } } },
              commentRun,
            ],
          };
        }
      }
      cellEdits[entryLoc] = cellValue;
    } else {
      cellEdits[entryLoc] = lang === "de" ? "fehlende Variablen" : "variables manquantes";
    }
  }

  // Process each template section
  const visibleRows = [];
  const emptyHeaderRows = []; // header rows where no tests are selected
  if (profileTestSet) {
    for (const section of templateSections) {
      const [firstRow, lastRow] = section.testRange;

      // Check if this section has any profile test rows
      let sectionHasTests = false;
      for (let row = firstRow; row <= lastRow; row++) {
        if (keepRows.has(row)) { sectionHasTests = true; break; }
      }

      if (sectionHasTests) {
        visibleRows.push(...section.headerRows);
        for (let row = firstRow; row <= lastRow; row++) {
          if (!keepRows.has(row)) hiddenRows.push(row);
        }
      } else {
        // No tests selected: hide section header AND all test rows
        hiddenRows.push(...section.headerRows);
        for (let row = firstRow; row <= lastRow; row++) {
          hiddenRows.push(row);
        }
      }
    }
  }

  return { cellEdits, hiddenRows, visibleRows, emptyHeaderRows, mergeRanges, skippedNoMapping };
};

// Convenience helpers re-exported for components that need to read a single
// test's per-language result location.
export const resolveResultLoc = (locField, lang) =>
  resolveLoc ? resolveLoc(locField, lang) : pickLoc(locField, lang);

export const pickPerLang = pickLoc;
